<script>
    import TextField from './TextFieldComponent.svelte';
    import Dropdown from './DropDownComponent.svelte';
    import DateTimePicker from './DateTimePickerComponent.svelte';
    import SingleSelectCheckboxArea from './SingleSelectCheckboxArea.svelte';

    let selectedSubject = '';
    let topicsOfInterest = '';
    let selectedClass = '';
    let datetimeValues = [''];
    let studentName = '';
    let studentEmail = '';
    let showSuccessNotification = false;
    let errorMessage = '';
    
    // User search functionality
    let searchTerm = '';
    let searchResults = [];
    let selectedUser = null;
    let isSearching = false;
    let registrationMode = 'custom'; // 'custom' or 'existing'

    const classChoices = [
        ['1', '1'],
        ['2', '2'],
        ['3', '3'],
        ['4', '4'],
        ['5', '5'],
        ['6', '6'],
        ['7', '7'],
        ['8', '8'],
        ['9', '9'],
        ['10', '10'],
        ['11', '11'],
        ['12', '12'],
        ['13', 'Universitetas'],
    ];

    const addDateTime = () => {
        datetimeValues = [...datetimeValues, ''];
    };

    const removeDateTime = (index) => {
        datetimeValues = datetimeValues.filter((_, i) => i !== index);
    };

    // Search for students
    async function searchStudents() {
        if (searchTerm.trim().length < 2) {
            searchResults = [];
            return;
        }
        
        isSearching = true;
        try {
            const response = await fetch('/studentplayground/api/admin/search-students/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`,
                },
                body: JSON.stringify({
                    search_term: searchTerm
                })
            });
            
            if (response.ok) {
                const data = await response.json();
                searchResults = data.students;
            } else {
                console.error('Failed to search students');
                searchResults = [];
            }
        } catch (error) {
            console.error('Error searching students:', error);
            searchResults = [];
        } finally {
            isSearching = false;
        }
    }
    
    // Select a user from search results
    function selectUser(user) {
        selectedUser = user;
        searchResults = [];
        searchTerm = `${user.first_name} ${user.last_name} (${user.username})`;
        registrationMode = 'existing';
        
        // Clear custom fields when selecting a user
        studentName = '';
        studentEmail = '';
    }
    
    // Switch to custom mode
    function switchToCustomMode() {
        registrationMode = 'custom';
        selectedUser = null;
        searchTerm = '';
        searchResults = [];
    }
    
    // Switch to existing user mode
    function switchToExistingMode() {
        registrationMode = 'existing';
        studentName = '';
        studentEmail = '';
    }

    const handleSubmit = async () => {
        // Reset error message
        errorMessage = '';
        
        // Validation
        if (registrationMode === 'custom') {
            if (!studentName.trim()) {
                errorMessage = 'Studento vardas yra privalomas.';
                return;
            }
        } else if (registrationMode === 'existing') {
            if (!selectedUser) {
                errorMessage = 'Prašome pasirinkti studentą iš paieškos rezultatų.';
                return;
            }
        }
        
        if (!selectedSubject) {
            errorMessage = 'Prašome pasirinkti bent vieną dalyką.';
            return;
        }
        
        if (!selectedClass) {
            errorMessage = 'Prašome pasirinkti klasę.';
            return;
        }

        const data = {
            subjects: [selectedSubject],
            topics: topicsOfInterest,
            selectedClass: selectedClass,
            datetimeValues: datetimeValues,
            // Include either user ID or custom student info
            ...(registrationMode === 'existing' ? {
                userId: selectedUser.id
            } : {
                studentName: studentName.trim(),
                studentEmail: studentEmail.trim() || undefined,
            })
        };
        
        const token = localStorage.getItem('jwtToken'); 
        try {
            const response = await fetch('/studentplayground/api/admin/create-class-registration/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': getCookie('csrftoken'),
                    'Authorization': `Bearer ${token}`
                },
                credentials: 'include',
                body: JSON.stringify(data)
            });

            if (response.ok) {
                // Show success notification
                showSuccessNotification = true;
                // Reset form
                resetForm();
                // Auto-hide notification after 5 seconds
                setTimeout(() => {
                    showSuccessNotification = false;
                }, 5000);
            } else {
                if (response.status === 401) {
                    alert('Reikia prisijungti iš naujo.');
                    localStorage.removeItem('jwtToken');
                    window.location.href = '#/about';
                } else {
                    const errorData = await response.json();
                    errorMessage = errorData.error || 'Klaida pateikiant formą!';
                }
            }
        } catch (error) {
            console.error('Error:', error);
            errorMessage = 'Klaida siunčiant duomenis. Bandykite dar kartą.';
        }
    };

    const resetForm = () => {
        selectedSubject = '';
        topicsOfInterest = '';
        selectedClass = '';
        datetimeValues = [''];
        studentName = '';
        studentEmail = '';
        selectedUser = null;
        searchTerm = '';
        searchResults = [];
        registrationMode = 'custom';
    };

    function getCookie(name) {
        let cookieValue = null;
        if (document.cookie && document.cookie !== '') {
            const cookies = document.cookie.split(';');
            for (let i = 0; i < cookies.length; i++) {
                const cookie = cookies[i].trim();
                if (cookie.substring(0, name.length + 1) === (name + '=')) {
                    cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                    break;
                }
            }
        }
        return cookieValue;
    }

    // Reactive search
    $: if (searchTerm && registrationMode === 'existing') {
        searchStudents();
    }
</script>

<!-- Success Notification -->
{#if showSuccessNotification}
    <div class="notification success-notification">
        <div class="notification-content">
            <span class="notification-icon">✓</span>
            <div class="notification-text">
                <strong>Registracija sėkminga!</strong>
                <p>Klasės registracija sukurta sėkmingai.</p>
            </div>
            <button class="close-notification" on:click={() => showSuccessNotification = false}>×</button>
        </div>
    </div>
{/if}

<!-- Error Message -->
{#if errorMessage}
    <div class="error-message">
        <span class="error-icon">⚠</span>
        <span>{errorMessage}</span>
        <button class="close-error" on:click={() => errorMessage = ''}>×</button>
    </div>
{/if}

<div class="admin-registration-form">
    <div class="form-header">
        <h2>Sukurti Klasės Registraciją</h2>
        <p class="form-description">Sukurkite naują klasės registracijos užklausą studentui</p>
    </div>

    <!-- Student Information Section -->
    <div class="form-section">
        <h3>Studento Informacija</h3>
        
        <!-- Registration Mode Selection -->
        <div class="mode-selection">
            <div class="mode-options">
                <label class="mode-option">
                    <input 
                        type="radio" 
                        bind:group={registrationMode} 
                        value="existing"
                        on:change={switchToExistingMode}
                    />
                    <span>Pasirinkti esamą studentą</span>
                </label>
                <label class="mode-option">
                    <input 
                        type="radio" 
                        bind:group={registrationMode} 
                        value="custom"
                        on:change={switchToCustomMode}
                    />
                    <span>Įvesti naują studento informaciją</span>
                </label>
            </div>
        </div>

        {#if registrationMode === 'existing'}
            <!-- User Search Section -->
            <div class="user-search-section">
                <div class="search-input-container">
                    <input
                        type="text"
                        bind:value={searchTerm}
                        placeholder="Ieškoti studentų pagal vardą, vartotojo vardą ar el. paštą..."
                        class="search-input"
                        disabled={selectedUser}
                    />
                    {#if isSearching}
                        <div class="search-spinner">🔍</div>
                    {/if}
                </div>
                
                <!-- Search Results -->
                {#if searchResults.length > 0 && !selectedUser}
                    <div class="search-results">
                        {#each searchResults as user}
                            <div 
                                class="search-result-item" 
                                tabindex="0"
                                role="button"
                                on:click={() => selectUser(user)}
                                on:keydown={(e) => e.key === 'Enter' || e.key === ' ' ? selectUser(user) : null}
                            >
                                <div class="student-info">
                                    <div class="student-name">{user.first_name} {user.last_name}</div>
                                    <div class="student-details">@{user.username} • {user.email}</div>
                                </div>
                            </div>
                        {/each}
                    </div>
                {/if}
                
                {#if searchResults.length === 0 && searchTerm.trim().length >= 2 && !isSearching && !selectedUser}
                    <div class="no-results">Nerasta studentų pagal "{searchTerm}"</div>
                {/if}

                <!-- Selected User Display -->
                {#if selectedUser}
                    <div class="selected-user-display">
                        <div class="selected-user-info">
                            <div class="student-name">{selectedUser.first_name} {selectedUser.last_name}</div>
                            <div class="student-details">@{selectedUser.username} • {selectedUser.email}</div>
                        </div>
                        <button type="button" class="change-user-button" on:click={switchToExistingMode}>
                            Keisti studentą
                        </button>
                    </div>
                {/if}
            </div>
        {:else}
            <!-- Custom Student Information -->
            <div class="custom-student-section">
                <TextField 
                    label="Studento vardas ir pavardė *" 
                    placeholder="Įveskite studento vardą ir pavardę" 
                    bind:value={studentName} 
                    required={true}
                />

                <TextField 
                    label="Studento el. paštas" 
                    placeholder="Įveskite studento el. pašto adresą (neprivaloma)" 
                    bind:value={studentEmail} 
                    type="email"
                />
            </div>
        {/if}
    </div>

    <!-- Class Information Section -->
    <div class="form-section">
        <h3>Klasės Informacija</h3>
        
        <SingleSelectCheckboxArea bind:selectedSubject={selectedSubject} />

        <TextField 
            label="Nuo kurios temos norėtumėte pradėti?" 
            placeholder="Įrašykite specifines temas, kurios domina studentą" 
            bind:value={topicsOfInterest} 
        />

        <Dropdown 
            label="Pasirinkite klasę *"
            options={classChoices}
            bind:selectedValue={selectedClass}
        />
    </div>

    <!-- Time Preferences Section -->
    <div class="form-section">
        <h3>Laiko Pasirinkiniai</h3>
        
        <!-- Dynamic DateTime Picker List -->
        {#each datetimeValues as datetimeValue, index}
            <DateTimePicker 
                id={`datetime-${index}`} 
                bind:value={datetimeValues[index]} 
                onRemove={() => removeDateTime(index)} 
            />
        {/each}
        
        <button type="button" class='addDatetime' on:click={addDateTime}>Pridėti laiką</button>
    </div>

    <div class="form-actions">
        <button on:click={resetForm} type="button" class="resetButton">Išvalyti formą</button>
        <button on:click={handleSubmit} type="button" class="submitButton">Sukurti Registraciją</button>
    </div>
</div>

<style>
    .admin-registration-form {
        max-width: 800px;
        margin: 0 auto;
        padding: 2rem;
        background: #fff;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    .form-header {
        text-align: center;
        margin-bottom: 2rem;
        padding-bottom: 1rem;
        border-bottom: 2px solid #e9ecef;
    }

    .form-header h2 {
        color: #2c3e50;
        margin: 0 0 0.5rem 0;
        font-size: 1.8rem;
        font-weight: 600;
    }

    .form-description {
        color: #6c757d;
        font-size: 1rem;
        margin: 0;
    }

    .form-section {
        margin-bottom: 2rem;
        padding: 1.5rem;
        background: #f8f9fa;
        border-radius: 6px;
        border-left: 4px solid #007bff;
    }

    .form-section h3 {
        color: #495057;
        margin: 0 0 1.5rem 0;
        font-size: 1.2rem;
        font-weight: 600;
    }

    /* Mode Selection Styles */
    .mode-selection {
        margin-bottom: 1.5rem;
        padding: 1rem;
        background: #fff;
        border-radius: 6px;
        border: 1px solid #dee2e6;
    }

    .mode-options {
        display: flex;
        gap: 2rem;
    }

    .mode-option {
        display: flex;
        align-items: center;
        cursor: pointer;
        font-size: 1rem;
        color: #495057;
    }

    .mode-option input[type="radio"] {
        margin-right: 0.5rem;
        transform: scale(1.2);
        cursor: pointer;
    }

    .mode-option span {
        user-select: none;
    }

    /* User Search Styles */
    .user-search-section {
        margin-top: 1rem;
    }

    .search-input-container {
        position: relative;
        margin-bottom: 1rem;
    }

    .search-input {
        width: 100%;
        padding: 12px 16px;
        border: 2px solid #dee2e6;
        border-radius: 6px;
        font-size: 1rem;
        box-sizing: border-box;
        transition: border-color 0.2s ease;
    }

    .search-input:focus {
        outline: none;
        border-color: #007bff;
    }

    .search-input:disabled {
        background-color: #f8f9fa;
        cursor: not-allowed;
    }

    .search-spinner {
        position: absolute;
        right: 12px;
        top: 50%;
        transform: translateY(-50%);
        font-size: 16px;
        animation: spin 1s linear infinite;
    }

    @keyframes spin {
        from { transform: translateY(-50%) rotate(0deg); }
        to { transform: translateY(-50%) rotate(360deg); }
    }

    .search-results {
        border: 1px solid #dee2e6;
        border-radius: 6px;
        max-height: 250px;
        overflow-y: auto;
        background: white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        margin-bottom: 1rem;
    }

    .search-result-item {
        padding: 12px 16px;
        border-bottom: 1px solid #eee;
        cursor: pointer;
        transition: background-color 0.2s ease;
        outline: none;
    }

    .search-result-item:hover,
    .search-result-item:focus {
        background-color: #f8f9fa;
    }

    .search-result-item:focus {
        box-shadow: inset 0 0 0 2px #007bff;
    }

    .search-result-item:last-child {
        border-bottom: none;
    }

    .student-info {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    .student-name {
        font-weight: 600;
        color: #212529;
        font-size: 1rem;
    }

    .student-details {
        color: #6c757d;
        font-size: 0.9rem;
    }

    .no-results {
        padding: 16px;
        text-align: center;
        color: #6c757d;
        font-style: italic;
        background: #f8f9fa;
        border: 1px solid #dee2e6;
        border-radius: 6px;
        margin-bottom: 1rem;
    }

    .selected-user-display {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px;
        background: #e3f2fd;
        border: 1px solid #2196f3;
        border-radius: 6px;
        margin-bottom: 1rem;
    }

    .selected-user-info {
        flex: 1;
    }

    .change-user-button {
        background: #007bff;
        color: white;
        border: none;
        padding: 8px 16px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 0.9rem;
        font-weight: 500;
        transition: background-color 0.2s ease;
    }

    .change-user-button:hover {
        background: #0056b3;
    }

    .custom-student-section {
        margin-top: 1rem;
    }

    .form-actions {
        display: flex;
        gap: 1rem;
        justify-content: flex-end;
        margin-top: 2rem;
        padding-top: 1.5rem;
        border-top: 1px solid #e9ecef;
    }

    .submitButton, .resetButton {
        padding: 12px 24px;
        border-radius: 6px;
        border: none;
        font-weight: 600;
        font-size: 1rem;
        cursor: pointer;
        transition: all 0.2s ease;
        min-width: 150px;
    }

    .submitButton {
        background-color: #28a745;
        color: white;
    }

    .submitButton:hover {
        background-color: #218838;
        transform: translateY(-1px);
        box-shadow: 0 4px 8px rgba(40, 167, 69, 0.3);
    }

    .resetButton {
        background-color: #6c757d;
        color: white;
    }

    .resetButton:hover {
        background-color: #5a6268;
        transform: translateY(-1px);
        box-shadow: 0 4px 8px rgba(108, 117, 125, 0.3);
    }
    
    .addDatetime {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 40px;
        background-color: #007bff;
        color: white;
        border: none;
        border-radius: 6px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
        margin-top: 1rem;
        width: 100%;
    }

    .addDatetime:hover {
        background-color: #0056b3;
        transform: translateY(-1px);
    }

    /* Notification Styles */
    .notification {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 1000;
        padding: 16px;
        animation: slideDown 0.3s ease-out;
    }

    .success-notification {
        background-color: #d4edda;
        border-bottom: 3px solid #28a745;
        color: #155724;
    }

    .notification-content {
        display: flex;
        align-items: center;
        max-width: 1200px;
        margin: 0 auto;
        gap: 12px;
    }

    .notification-icon {
        font-size: 24px;
        font-weight: bold;
        color: #28a745;
        background-color: white;
        border-radius: 50%;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .notification-text {
        flex: 1;
    }

    .notification-text strong {
        display: block;
        font-size: 16px;
        margin-bottom: 4px;
    }

    .notification-text p {
        margin: 0;
        font-size: 14px;
        opacity: 0.9;
    }

    .close-notification {
        background: none;
        border: none;
        font-size: 24px;
        font-weight: bold;
        cursor: pointer;
        color: #155724;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        transition: background-color 0.2s ease;
        margin: 0;
        padding: 0;
        flex-shrink: 0;
    }

    .close-notification:hover {
        background-color: rgba(21, 87, 36, 0.1);
    }

    /* Error Message Styles */
    .error-message {
        background-color: #f8d7da;
        color: #721c24;
        border: 1px solid #f5c6cb;
        border-radius: 6px;
        padding: 12px 16px;
        margin-bottom: 1rem;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .error-icon {
        font-size: 18px;
        color: #dc3545;
        flex-shrink: 0;
    }

    .close-error {
        background: none;
        border: none;
        font-size: 18px;
        font-weight: bold;
        cursor: pointer;
        color: #721c24;
        margin-left: auto;
        padding: 0;
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 2px;
        transition: background-color 0.2s ease;
    }

    .close-error:hover {
        background-color: rgba(114, 28, 36, 0.1);
    }

    @keyframes slideDown {
        from {
            transform: translateY(-100%);
            opacity: 0;
        }
        to {
            transform: translateY(0);
            opacity: 1;
        }
    }

    /* Responsive Design */
    @media (max-width: 768px) {
        .admin-registration-form {
            margin: 1rem;
            padding: 1rem;
        }

        .form-actions {
            flex-direction: column;
        }

        .submitButton, .resetButton {
            width: 100%;
        }
    }
</style>
